import {
  Control,
  Icon,
  LatLngBounds,
  LayerGroup,
  LeafletMouseEvent,
  Map,
  Marker,
  Polyline,
  TileLayer,
}                                    from 'leaflet';
import store                         from '@/store';
import IPosition                     from '@/interfaces/IPosition';
import GeolocationService            from '@/services/GeolocationService';
import { IFeatureCollection }        from '@/interfaces/IFeatureCollection';
// import '@/../node_modules/prunecluster/dist/LeafletStyleSheet.css';
import ParkingLayerService           from '@/services/layers/ParkingsLayerService';
import ParkingZoneLayerService       from '@/services/layers/ParkingZoneLayerService';
import ParkingZonePointsLayerService from '@/services/layers/ParkingZonePointsLayerService';
import ParkingMetersLayerService     from '@/services/layers/ParkingMetersLayerService';
import ParkingSpotsService           from '@/services/layers/ParkingSpotsService';
import ParkingGroupsLayerService     from '@/services/layers/ParkingGroupsLayerService';
import EventsLayerService            from '@/services/layers/EventsLayerService';
import WeatherStationsLayerService   from '@/services/layers/WeatherStationsLayerService';
import VmsBoardsLayerService         from '@/services/layers/VmsBoardsLayerService';
import CamerasLayerService         from '@/services/layers/CamerasLayerService';
import TrafficLayerService         from '@/services/layers/TrafficLayerService';
import TrafficPointsLayerService         from '@/services/layers/TrafficPointsLayerService';
import PoiPointsLayerService         from '@/services/layers/PoiPointsLayerService';
/*eslint-disable */
import TrackStartPointsLayerService         from '@/services/layers/TrackStartPointsLayerService';
import TrackEndPointsLayerService         from '@/services/layers/TrackEndPointsLayerService';

import Zoom = Control.Zoom;
import ApiPlanerService from './ApiPlanerService';
// @ts-ignore
import TrackLineLayerService from './layers/TrackLineLayerService';
import { toRaw } from 'vue';
import BikeStationsLayerService from './layers/BikeStationsLayerService';
import ParkingBoardsLayerService from './layers/ParkingBoardsLayerService';
import RadarBoardsLayerService from './layers/RadarBoardsLayerService';

/**
 * A class for abstracting away manipulations of leaflet map instance;
 */
export default class MapService {
  /**
   * Map an instance of leaflet
   */
  private mapReference: Map;

  private themeUrls = [
    'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    'https://cartodb-basemaps-{s}.global.ssl.fastly.net/dark_all/{z}/{x}/{y}.png',
    // 'https://tiles.stadiamaps.com/tiles/alidade_smooth_dark/{z}/{x}/{y}{r}.png',
    
    // 'https://stamen-tiles.a.ssl.fastly.net/toner/{z}/{x}/{y}.png',
  ];

  public getThemeUrl = (): string => {
    return this.themeUrls[0];
  };

  /**
   *
   * @param leafletMapInstance Map an instance of leaflet that will be manipulated using this class
   */
  constructor(leafletMapInstance: Map) {
    this.mapReference = toRaw(leafletMapInstance);
    // this.mapReference = leafletMapInstance;
  }

  /**
   * Initialises the map, should be fired only once per map instance.
   */
  public initializeMap = (): void => {
    this.setTileLayer();
    this.setBounds();
    this.bindEvents();
    this.mapReference.addControl(new Zoom({
      position: 'topright',
    }));
  };

  public changeMapContrast = (): void => {
    this.mapReference.eachLayer((layer) => {
      if (layer instanceof TileLayer) {
        layer.setUrl(this.getThemeUrl());
      }
    });
  };

  /**
   * Binds store updating events
   */
  public bindEvents = (): void => {
    this.mapReference.on('moveend', (event) => {
      store.commit('updatePosition', event.target.getCenter());
    });

    this.mapReference.on('zoomend', (event) => {
      store.commit('updateZoomFactor', event.target.getZoom());
    });
  };

  /**
   * Sets the provider of map tiles
   */
  private setTileLayer = (): void => {
    const tileLayer = new TileLayer(this.getThemeUrl(), {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxNativeZoom: 19,
      maxZoom: 21,
      minZoom: 8,
    });
    this.mapReference.addLayer(toRaw(tileLayer));
  };

  /**
   * Sets the position and the zoom factor of the map.
   * @param {IPosition} position Lat and lng of the map view center.
   * @param {number} zoom Zoom factor of the map.
   */
  public setPostition = (position: IPosition, zoom: number = 15): void => {
    this.mapReference.setView([position.lat, position.lng], zoom);
  };

  /**
   * Sets a geolocation marker on the map using set position.
   * @param {IPosition} position Position at which the marker will be set.
   */
  public setGeolocationPositionMarker = (position: IPosition): Marker => {
    const positionMarker = new Marker([position.lat, position.lng], {
      icon: new Icon({
        // eslint-disable-next-line global-require
        iconUrl: require('@/assets/markers/icon-map-position.svg'),
        iconSize: [40, 40],
        iconAnchor: [20, 20],
        popupAnchor: [-3, -76],
      }),
    });
    this.mapReference.addLayer(positionMarker);

    return positionMarker;
  };

  /**
   * Sets a marker on the map showing the selected position.
   * @param {IPosition} position Position at which the marker will be set.
   */
  public setSelectedPositionMarker = (position: IPosition): Marker => {
    const positionMarker = new Marker([position.lat, position.lng], {
      icon: new Icon({
        // eslint-disable-next-line global-require
        iconUrl: require('@/assets/markers/marker-location.svg'),
        iconSize: [40, 40],
        iconAnchor: [20, 20],
        popupAnchor: [-3, -76],
      }),
    });
    // this.mapReference.addLayer(positionMarker);

    return positionMarker;
  };

  /**
   * Sets the default boundaries of the map.
   */
  public setBounds = (): void => {
    const mapBounds = new LatLngBounds([
      [50.126674, 21.077248],
      [49.926674, 20.877248],
    ]);

    // this.mapReference.setMaxBounds(mapBounds);
    this.mapReference.on('drag', () => {
      // this.mapReference.panInsideBounds(mapBounds, { animate: false });
    });
  };

  /**
   * Sets the position marker from using geolocation data.
   */
  public setPositionFromGeolocation = (): Promise<any> => GeolocationService.getCurrentPosition()
    .then((value) => {
      const geolocationPositionMarker: Marker = this.setSelectedPositionMarker({
        lat: value.coords.latitude,
        lng: value.coords.longitude,
      });
      this.setMarkerAndEndPositionSetMode(geolocationPositionMarker);
    });

  /**
   * Initialises the position set mode for the leaflet map.
   */
  public startPositionSetMode = (): void => {
    this.mapReference.on('click', (event: LeafletMouseEvent) => {
      const userPositionMarker: Marker = this.setSelectedPositionMarker(event.latlng);
      // this.setMarkerAndEndPositionSetMode(userPositionMarker);
    });
  };

  /**
   * Ends the manual position setting mode on leaflet map.
   */
  public endPositionSetMode = (): void => {
    this.mapReference.off('click');
  };

  /**
   * Updates the position marker in the store and ends manual position setting mode.
   * @param {Marker} marker Marker to be pushed to the store.
   */
  private setMarkerAndEndPositionSetMode = (marker: Marker): void => {
    // store.state.userPositionMarker.remove();
    store.commit('updateUserPositionMarker', marker);
    store.commit('endLocationMode');
    this.endPositionSetMode();
  };

    /**
   * Draws parking zones center markers on the map based on vuex store data.
   */
    // public drawParkingZonesMarkers = (): LayerGroup => {
    //   const parkingZonePointsLayerService: ParkingZonePointsLayerService =
    //     new ParkingZonePointsLayerService();
    //   const parkingZonesMarkersLayer: LayerGroup = parkingZonePointsLayerService.redrawLayer();
    //   this.mapReference.addLayer(parkingZonesMarkersLayer);
  
    //   return parkingZonesMarkersLayer;
    // };

  /**
   * Updates the map with the marker data from the store.
   */
  public drawParkingsMarkers = (): LayerGroup => {
    const parkingLayerService: ParkingLayerService = new ParkingLayerService();
    const parkingsMarkersLayer: LayerGroup = parkingLayerService.redrawLayer();
    this.mapReference.addLayer(toRaw(parkingsMarkersLayer));

    return toRaw(parkingsMarkersLayer);
  };

  // /**
  //  * Draws parking zones on the map based on vuex store data.
  //  */
  public drawParkingZonesPolygons = (): LayerGroup => {
    const parkingZoneLayerService = new ParkingZoneLayerService();
    const parkingZonesPolygonsLayer = parkingZoneLayerService.redrawLayer();
    this.mapReference.addLayer(toRaw(parkingZonesPolygonsLayer));

    return toRaw(parkingZonesPolygonsLayer);
  };

  /**
   * Draws parkingmeters on the map based on vuex store data.
   */
  public drawParkingMetersMarkers = (): LayerGroup => {
    const parkingMetersLayerService = new ParkingMetersLayerService();
    const parkingMetersPolygonsLayer = parkingMetersLayerService.redrawLayer();
    this.mapReference.addLayer(toRaw(parkingMetersPolygonsLayer));

    return toRaw(parkingMetersPolygonsLayer);
  };

  public drawParkingBoardsMarkers = (): LayerGroup => {
    const parkingBoardsLayerService = new ParkingBoardsLayerService();
    const parkingBoardsPolygonsLayer = parkingBoardsLayerService.redrawLayer();
    this.mapReference.addLayer(toRaw(parkingBoardsPolygonsLayer));

    return toRaw(parkingBoardsPolygonsLayer);
  };

  /**
   * Draws parking spots on the map based on vuex store data.
   */
  public drawParkingSpotsMarkers = (): LayerGroup => {
    const parkingSpotsService = new ParkingSpotsService();
    const parkingSpotsMarkersLayer = parkingSpotsService.redrawLayer();
    this.mapReference.addLayer(parkingSpotsMarkersLayer);

    return parkingSpotsMarkersLayer;
  };

  /**
   * Draws parking groups on the map based on vuex store data.
   */
  public drawParkingGroupsPolygons = (): LayerGroup => {
    const parkingGroupsLayerService = new ParkingGroupsLayerService();
    const parkingGroupsPolygonsLayer = parkingGroupsLayerService.redrawLayer();
    this.mapReference.addLayer(toRaw(parkingGroupsPolygonsLayer));

    return toRaw(parkingGroupsPolygonsLayer);
  };

  /**
   * Draws events on the map based on the vuex store data and selected category.
   * @param category {string} event category to be rendered
   */
  public drawEventsMarkers = (): LayerGroup => {
    const eventsLayerService = new EventsLayerService();
    const eventsLayer = eventsLayerService.redrawLayer();
    this.mapReference.addLayer(toRaw(eventsLayer));

    return toRaw(eventsLayer);
  };

  /**
   * Draws weather stations on the map based on vuex store data.
   */
  public drawWeatherStationsMarkers = (): LayerGroup => {
    const weatherStationsLayerService = new WeatherStationsLayerService();
    const weatherStationsPolygonsLayer = weatherStationsLayerService.redrawLayer();
    this.mapReference.addLayer(toRaw(weatherStationsPolygonsLayer));

    return toRaw(weatherStationsPolygonsLayer);
  };

  /**
   * Draws VMS boards on the map based on vuex store data.
   */
  public drawVmsBoardsMarkers = (): LayerGroup => {
    const vmsBoardsLayerService = new VmsBoardsLayerService();
    const vmsBoardsPolygonsLayer = vmsBoardsLayerService.redrawLayer();
    this.mapReference.addLayer(toRaw(vmsBoardsPolygonsLayer));

    return toRaw(vmsBoardsPolygonsLayer);
  };

  /**
   * Draws cameras on the map based on vuex store data.
   */
  public drawCamerasMarkers = (type: string): LayerGroup => {
    const camerasLayerService = new CamerasLayerService();
    const camerasPolygonsLayer = camerasLayerService.redrawLayer(type);
    this.mapReference.addLayer(toRaw(camerasPolygonsLayer));

    return (camerasPolygonsLayer);
  };

  /**
   * Draws traffic lines on the map based on vuex store data.
   */
  public drawTrafficPolyLines = (): LayerGroup => {
    const trafficLayerService = new TrafficLayerService();
    const trafficPolylinesLayer = trafficLayerService.redrawLayer();
    this.mapReference.addLayer(toRaw(trafficPolylinesLayer));

    return toRaw(trafficPolylinesLayer);
  };

  /**
   * Draws traffic points on the map based on vuex store data.
   */
   public drawTrafficPoints = (): LayerGroup => {
    const trafficPointsLayerService = new TrafficPointsLayerService();
    const trafficPointsLayer = trafficPointsLayerService.redrawLayer();
    this.mapReference.addLayer(toRaw(trafficPointsLayer));

    return toRaw(trafficPointsLayer);
  };

  /**
   * Draws POI markers on the map based on vuex store data.
   */
   public drawPoiMarkers = (): LayerGroup => {
    const poiPointsLayerService = new PoiPointsLayerService();
    const poiPointsLayer = poiPointsLayerService.redrawLayer();
    this.mapReference.addLayer(toRaw(poiPointsLayer));

    return toRaw(poiPointsLayer);
  };

  public drawTrackStartPoints = (): LayerGroup => {
    const trackStartPointsLayerService = new TrackStartPointsLayerService();
    const trackStartPointsPolygonsLayer = trackStartPointsLayerService.redrawLayer();
    this.mapReference.addLayer(toRaw(trackStartPointsPolygonsLayer));

    return toRaw(trackStartPointsPolygonsLayer);
  };

  public drawTrackEndPoints = (): LayerGroup => {
    const trackEndPointsLayerService = new TrackEndPointsLayerService();
    const trackEndPointsPolygonsLayer = trackEndPointsLayerService.redrawLayer();
    this.mapReference.addLayer(toRaw(trackEndPointsPolygonsLayer));

    return toRaw(trackEndPointsPolygonsLayer);
  };

  public drawTrackLines = (): LayerGroup => {
    const trackLineLayerService = new TrackLineLayerService();
    const trackPolylinesLayer = trackLineLayerService.redrawLayer();
    
    this.mapReference.addLayer(toRaw(trackPolylinesLayer));

    return toRaw(trackPolylinesLayer);
  };

  public drawBikeStationsMarkers = (): LayerGroup => {
    const bikeStationsLayerService = new BikeStationsLayerService();
    const bikeStationsPolygonsLayer = bikeStationsLayerService.redrawLayer();
    this.mapReference.addLayer(toRaw(bikeStationsPolygonsLayer));

    return toRaw(bikeStationsPolygonsLayer);
  };

  public drawRadarBoardsMarkers = (): LayerGroup => {
    const radarBoardsLayerService = new RadarBoardsLayerService();
    const radarBoardsPolygonsLayer = radarBoardsLayerService.redrawLayer();
    this.mapReference.addLayer(toRaw(radarBoardsPolygonsLayer));

    return toRaw(radarBoardsPolygonsLayer);
  };
}
